
import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UseFormReturn } from "react-hook-form";
import { TerraceFormValues } from "../SubmissionFormSchema";

interface PhotoUrlExtractorProps {
  form: UseFormReturn<TerraceFormValues>;
}

const PhotoUrlExtractor: React.FC<PhotoUrlExtractorProps> = ({ form }) => {
  const [mapsPhotoUrl, setMapsPhotoUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);

  const handleExtractMapsPhoto = async () => {
    if (!mapsPhotoUrl.trim()) {
      toast.error('Please enter a Google Maps photo URL');
      return;
    }

    setIsExtracting(true);
    try {
      console.log('Extracting photo URL from:', mapsPhotoUrl);
      
      const { data, error } = await supabase.functions.invoke('extract-maps-photo-url', {
        body: {
          mapsPhotoUrl: mapsPhotoUrl.trim(),
          maxWidth: 800,
          maxHeight: 600
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Failed to extract photo URL');
      }

      if (data?.success && data?.directUrl) {
        form.setValue('photo_url', data.directUrl);
        toast.success('Photo URL extracted successfully!');
        setMapsPhotoUrl(''); // Clear the input after success
      } else {
        throw new Error(data?.error || 'Could not extract photo URL from the provided Google Maps URL');
      }
    } catch (error) {
      console.error('Error extracting photo URL:', error);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to extract photo URL';
      if (error.message?.includes('place ID')) {
        errorMessage = 'Could not find a valid place ID in the Google Maps URL. Please make sure you\'re using a direct link to a place with photos.';
      } else if (error.message?.includes('No photos')) {
        errorMessage = 'No photos found for this place. The location might not have any photos uploaded to Google Maps.';
      } else if (error.message?.includes('API key')) {
        errorMessage = 'Google Places API key is not configured. Please contact the administrator.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Card className="card-warm">
      <CardContent className="p-4">
        <div className="text-sm font-medium text-sunset-dark mb-3 flex items-center gap-2">
          <ExternalLink className="h-4 w-4" />
          Extract Photo from Google Maps URL
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Paste a Google Maps place URL to extract the photo using Google Places API
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="https://maps.google.com/maps/place/... (place URL)"
            value={mapsPhotoUrl}
            onChange={(e) => setMapsPhotoUrl(e.target.value)}
            className="flex-1"
          />
          <Button
            type="button"
            onClick={handleExtractMapsPhoto}
            disabled={isExtracting || !mapsPhotoUrl.trim()}
            variant="outline"
          >
            {isExtracting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Extract
              </>
            )}
          </Button>
        </div>
        {isExtracting && (
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Extracting photo using Google Places API...
          </div>
        )}
        <div className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
          <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <div>
            Use a direct Google Maps place URL (not just a photo URL). The system will find the place and extract the first available photo.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoUrlExtractor;
